import axios from "axios";
import { API_BASE_URL, AUTH_ENDPOINTS } from "../config/apiConfig";

// Create axios instance with base URL
const baseAxios = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

//comment

// Token refresh lock to prevent race conditions
let isRefreshing = false;
let refreshSubscribers = [];

// Function to add callbacks to be executed after token refresh
const subscribeToTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Function to execute all callbacks after token refresh
const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Function to check if token is expired
export const isTokenExpired = (token) => {
  try {
    // Get payload from JWT token
    const payload = JSON.parse(atob(token.split(".")[1]));
    // Check if token is expired
    return payload.exp * 1000 < Date.now();
  } catch (error) {
    return true; // If there's any error, treat token as expired
  }
};

// Interceptor to handle token refreshing
baseAxios.interceptors.request.use(
  async (config) => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (user?.token) {
      // Check if token is expired and needs refresh
      if (isTokenExpired(user.token) && user.refreshToken) {
        // If a refresh is already in progress, wait for it to complete
        if (isRefreshing) {
          return new Promise((resolve) => {
            subscribeToTokenRefresh((token) => {
              config.headers.Authorization = `Bearer ${token}`;
              resolve(config);
            });
          });
        }

        isRefreshing = true;

        try {
          // Try to refresh the token
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh-token`,
            {
              refreshToken: user.refreshToken,
            }
          );

          // Update token in localStorage
          const updatedUser = {
            ...user,
            token: response.data.token,
            refreshToken: response.data.refreshToken || user.refreshToken,
          };
          localStorage.setItem("user", JSON.stringify(updatedUser));

          // Set the new token in the request
          config.headers.Authorization = `Bearer ${response.data.token}`;

          // Notify all waiting requests that token has been refreshed
          onTokenRefreshed(response.data.token);
        } catch (error) {
          // If refresh fails, log out user
          localStorage.removeItem("user");
          refreshSubscribers = []; // Clear subscribers
          window.location.href = "/signin";
        } finally {
          isRefreshing = false;
        }
      } else {
        // Token is still valid, use it
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle deleted user accounts
baseAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check for the specific "deleted user" error
    if (
      error.response?.status === 401 &&
      error.response?.data?.error === "User no longer exists"
    ) {
      // Clear auth data
      localStorage.removeItem("user");

      // Redirect to login with a message
      window.location.href = "/signin?accountDeleted=true";
    }

    return Promise.reject(error);
  }
);

/**
 * Create an axios instance with authentication headers
 * @returns {Object} Axios instance with auth headers
 */
export const createAuthAxios = () => {
  const user = JSON.parse(localStorage.getItem("user"));

  if (user?.token) {
    // Create a new instance to ensure headers are properly set
    const authInstance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.token}`,
      },
    });

    // Add request interceptor to ensure token is always fresh
    authInstance.interceptors.request.use((config) => {
      // Get the latest token in case it was refreshed
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (currentUser?.token) {
        config.headers.Authorization = `Bearer ${currentUser.token}`;
      }
      return config;
    });

    return authInstance;
  }

  return baseAxios;
};

/**
 * Handle API errors consistently with enhanced security features
 * @param {Error} error - Axios error object
 * @param {Function} showNotification - Optional notification function
 * @param {Function} showFieldError - Optional field error function
 * @returns {Object} Standardized error object
 */
export const handleApiError = (
  error,
  showNotification = null,
  showFieldError = null
) => {
  const errorResponse = {
    message: "An unexpected error occurred",
    status: 500,
    data: null,
  };

  if (error.response) {
    // Server responded with a status code outside of 2xx range
    errorResponse.message = error.response.data.message || error.message;
    errorResponse.status = error.response.status;
    errorResponse.data = error.response.data;

    // 🚨 RATE LIMITING ERROR HANDLING
    if (error.response.status === 429) {
      const retryAfter = error.response.headers["retry-after"] || 60;
      const message = `Rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`;
      errorResponse.message = message;

      if (showNotification) {
        showNotification("Rate limit exceeded", message);
      }
      return errorResponse;
    }

    // ✅ ENHANCED VALIDATION FEEDBACK
    if (error.response.status === 400 && error.response.data.errors) {
      const validationErrors = error.response.data.errors;
      errorResponse.validationErrors = validationErrors;

      if (showFieldError && Array.isArray(validationErrors)) {
        validationErrors.forEach((err) => {
          showFieldError(err.path, err.msg);
        });
      }

      // Set a user-friendly message for validation errors
      errorResponse.message = "Please check the form for validation errors.";
      return errorResponse;
    }

    // If unauthorized, clear user data and redirect to login
    if (error.response.status === 401) {
      localStorage.removeItem("user");
    }
  } else if (error.request) {
    // Request was made but no response received
    errorResponse.message = "No response received from server";
    errorResponse.status = 0;
  } else {
    // Something happened in setting up the request
    errorResponse.message = error.message;
  }

  return errorResponse;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export const isAuthenticated = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  return !!user?.token && !isTokenExpired(user.token);
};

/**
 * Get authentication token from localStorage
 * Robust implementation that handles various storage scenarios
 * @returns {string|null} Auth token or null if not authenticated
 */
export const getAuthToken = () => {
  try {
    // Primary: Try to get token from user object
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user?.token) {
        return user.token;
      }
      // Some implementations use accessToken
      if (user?.accessToken) {
        return user.accessToken;
      }
    }

    // Fallback: Try direct token storage
    const directToken = localStorage.getItem("token");
    if (directToken) {
      return directToken;
    }

    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      return accessToken;
    }

    return null;
  } catch (error) {
return null;
  }
};

/**
 * Check if user needs to complete onboarding
 * @param {Object} user - User object
 * @returns {boolean} True if user needs to complete onboarding
 */
export const needsOnboarding = (user) => {
  return !user?.onboardingCompleted;
};

// Utility functions for API interactions
